import React, { useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { formatDollars } from '@/utils/formatDollars';
import { flashElement } from '@/utils/animationHelpers';
import type { PayBucket } from '@/utils/rateCalculations';

/* ------------------------------------------------------------------ */
/*  BucketBreakdown                                                    */
/* ------------------------------------------------------------------ */

export interface BucketBreakdownProps {
  buckets: PayBucket[];
  totalLabel: string;
  totalAmount: number;
  variant: 'normal' | 'surcharged';
  roleKey?: string | number;
}

/** Render a customLabel string, wrapping dollar amounts in a targetable span. */
function renderCustomLabel(label: string) {
  const match = label.match(/^(.*?)(\$[\d,.]+)(.*?)$/);
  if (!match) return label;
  return <>{match[1]}<span data-label-value>{match[2]}</span>{match[3]}</>;
}

/**
 * Renders a pay-bucket breakdown with per-tier line items and a total.
 *
 * Uses inline `style` for text colors instead of Tailwind utilities to work
 * around a Tailwind v4 bug where `text-*` color utilities don't resolve
 * inside certain button/container elements.
 *
 * When the surcharged variant contains buckets with `lifted: true` (floor mode),
 * lifted rows render in amber with a ↑ origin annotation, while unchanged rows
 * render dimmed. Non-floor surcharge methods are unaffected.
 *
 * Dollar amounts and labels animate (fadeSlideIn) only when their value actually
 * changes between renders triggered by a roleKey change.
 */
export const BucketBreakdown: React.FC<BucketBreakdownProps> = ({
  buckets,
  totalLabel,
  totalAmount,
  variant,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const prevAmounts = useRef<number[] | null>(null);
  const prevLabels = useRef<(string | undefined)[] | null>(null);
  const prevTotal = useRef<number | null>(null);

  // Serialize bucket data for the dependency array
  const amountsSig = buckets.map(b => b.amount).join(',');
  const labelsSig = buckets.map(b => b.customLabel ?? '').join(',');

  // Animate only values that actually changed (skip first render)
  useEffect(() => {
    const el = containerRef.current;
    if (!el || prevAmounts.current === null) {
      // First render — record initial values, don't animate
      prevAmounts.current = buckets.map(b => b.amount);
      prevLabels.current = buckets.map(b => b.customLabel);
      prevTotal.current = totalAmount;
      return;
    }

    // Flash changed values — for labels, target only the dollar value span
    const labels = el.querySelectorAll<HTMLElement>('[data-label]');
    const amounts = el.querySelectorAll<HTMLElement>('[data-amount]');

    buckets.forEach((b, i) => {
      if (labels[i] && prevLabels.current![i] !== b.customLabel) {
        const valueSpan = labels[i].querySelector<HTMLElement>('[data-label-value]');
        flashElement(valueSpan ?? labels[i]);
      }
      if (amounts[i] && prevAmounts.current![i] !== b.amount) flashElement(amounts[i]);
    });

    // Flash total if changed
    const totalEl = el.querySelector<HTMLElement>('[data-total]');
    if (totalEl && prevTotal.current !== totalAmount) flashElement(totalEl);

    // Update refs after comparison
    prevAmounts.current = buckets.map(b => b.amount);
    prevLabels.current = buckets.map(b => b.customLabel);
    prevTotal.current = totalAmount;
  }, [amountsSig, labelsSig, totalAmount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Floor mode (Guaranteed Rate): lifted buckets have liftedFrom set, indicating
  // a tier replacement. Non-lifted buckets dim to draw attention to the change.
  // Premium mode (Hourly Premium): lifted buckets have no liftedFrom — they're
  // additive premium lines that get amber without dimming siblings.
  const isFloorMode = variant === 'surcharged' && buckets.some(b => b.lifted && b.liftedFrom);

  return (
    <div
      ref={containerRef}
      className={cn(
        'rounded-[0.25rem] px-2 py-2 mb-2',
        variant === 'surcharged'
          ? 'bg-primary/[0.05] border border-primary/[0.12]'
          : 'bg-[rgba(0,0,0,0.15)] shadow-[inset_0_1px_2px_rgba(0,0,0,0.15)]',
      )}
    >
      {buckets.map((bucket, idx) => {
        // Lifted buckets render in amber. In floor mode (Guaranteed Rate),
        // non-lifted buckets are dimmed. Outside floor mode (e.g. Hourly Premium),
        // lifted buckets get amber without dimming siblings.
        const isLifted = bucket.lifted;
        const isDimmedByFloor = isFloorMode && !bucket.lifted;

        const tierColor = variant === 'normal' || isDimmedByFloor
          ? 'color-mix(in oklab, var(--color-muted-foreground) 45%, transparent)'
          : isLifted
          ? 'var(--primary-text)'
          : 'color-mix(in oklab, var(--color-foreground) 70%, transparent)';

        const amountColor = variant === 'normal' || isDimmedByFloor
          ? 'color-mix(in oklab, var(--color-muted-foreground) 45%, transparent)'
          : undefined; // defaults to text-foreground via className

        return (
          <div key={`${bucket.tierName}-${bucket.multiplier}-${idx}`} className="flex items-baseline justify-between gap-2 mb-1">
            <span
              data-label
              className="font-mono"
              style={{ fontSize: 10, color: tierColor }}
            >
              {bucket.customLabel ? renderCustomLabel(bucket.customLabel) : <>{bucket.hours}h &times; {bucket.tierName} ({bucket.multiplier}x)</>}
              {isLifted && isFloorMode && (
                <span className="text-primary-text ml-1">&uarr;</span>
              )}
              {isLifted && isFloorMode && bucket.liftedFrom && (
                <span style={{ color: 'color-mix(in oklab, var(--color-muted-foreground) 35%, transparent)', marginLeft: 4, fontSize: 9 }}>
                  from {bucket.liftedFrom}
                </span>
              )}
              {bucket.annotation && !isLifted && !isFloorMode && (
                <span style={{ color: 'color-mix(in oklab, var(--color-muted-foreground) 30%, transparent)', marginLeft: 4, fontSize: 'inherit' }}>{bucket.annotation}</span>
              )}
            </span>
            <span
              data-amount
              className={cn(
                'font-mono text-[10px] sm:text-[12px] font-medium tabular-nums',
                !amountColor && (variant === 'normal' ? 'text-muted-foreground/45' : 'text-foreground'),
              )}
              style={amountColor ? { color: amountColor } : undefined}
            >
              {formatDollars(bucket.amount)}
            </span>
          </div>
        );
      })}
      <div
        className={cn(
          'flex items-baseline justify-between gap-2 pt-1 mt-1',
          'border-t',
          variant === 'surcharged' ? 'border-primary/20' : 'border-foreground/20',
        )}
      >
        <span
          className="font-label"
          style={{
            fontSize: 10,
            color: variant === 'normal'
              ? 'color-mix(in oklab, var(--color-muted-foreground) 45%, transparent)'
              : 'color-mix(in oklab, var(--color-foreground) 70%, transparent)',
          }}
        >
          {totalLabel}
        </span>
        <span
          data-total
          className={cn(
            'font-mono text-[10px] sm:text-[12px] font-bold tabular-nums',
            variant === 'normal' ? 'text-muted-foreground/45' : 'text-foreground',
          )}
        >
          {formatDollars(totalAmount)}
        </span>
      </div>
    </div>
  );
};
